import React, { useState, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTickets } from '../../../contexts/TicketContext';
import { usePunishment } from '../../../contexts/PunishmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
    Check,
    ChevronsUpDown,
    BarChart3,
    User,
    Calendar,
    MessageSquare,
    CheckCircle2,
    Clock,
    Ticket,
    AlertTriangle,
    TrendingUp,
    Target,
    Star,
    Shield,
    Users,
    Activity,
    Info
} from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { format, isSameDay, isSameWeek, isSameMonth, parseISO, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getStaffDisplayName } from '../../../utils/staffDisplay';

const IndividualTab = () => {
    const { tickets } = useTickets();
    const { allUsers, user: currentUser } = useAuth();
    const { punishments } = usePunishment();

    // State for filtering
    const [dateFilter, setDateFilter] = useState('monthly'); // 'daily', 'weekly', 'monthly', 'all'
    const [selectedDate, setSelectedDate] = useState(new Date());

    // Staff Selection
    const [openCombobox, setOpenCombobox] = useState(false);
    const [selectedStaffId, setSelectedStaffId] = useState(currentUser?.discordId || "");

    // 1. Get List of Staff
    const staffList = useMemo(() => {
        const staff = [];
        if (allUsers) {
            allUsers.forEach(u => {
                if (u.accessLevel !== 'user') staff.push(u);
            });
        }
        return staff;
    }, [allUsers]);

    const selectedUser = staffList.find(s => s.discordId === selectedStaffId) || currentUser;

    // 2. Metrics Calculation
    const metrics = useMemo(() => {
        if (!selectedUser) return null;
        const userId = selectedUser.discordId;
        const userName = selectedUser.name;
        const staffDisplayName = getStaffDisplayName(selectedUser);

        // --- Date Filtering Logic ---
        const filterDate = (dateStr) => {
            if (!dateStr) return false;
            const date = new Date(dateStr);
            if (dateFilter === 'all') return true;
            if (dateFilter === 'daily') return isSameDay(date, selectedDate);
            if (dateFilter === 'weekly') return isSameWeek(date, selectedDate, { locale: ptBR });
            if (dateFilter === 'monthly') return isSameMonth(date, selectedDate);
            return true;
        };

        let stats = {
            open: 0,
            finished: 0,
            waitingUser: 0,
            lastClosedAfterUserMsg: null,
            totalAssigned: 0,
            totalHandledPeriod: 0,
            totalResponseTime: 0,
            firstResponseCount: 0,
            totalResolutionTime: 0,
            resolutionCount: 0,
            reopened: 0,
            ratings: { count: 0, positive: 0, negative: 0, sum: 0 },
            totalMessages: 0,
            uniqueUsers: new Set(),
            myTicketsCount: 0,
            // SLA Metrics
            slaFirstResponseBreaches: 0,
            slaResolutionBreaches: 0,
            slaFirstResponseTotal: 0, // Total eligible for first response SLA
            slaResolutionTotal: 0,    // Total eligible for resolution SLA
            totalSlaDelay: 0,         // Total delay in ms
            ticketsBreachedSlaCount: 0, // Unique tickets that breached any SLA
            // Advanced Metrics
            ticketsOneResponse: 0,
            ticketsMultipleInteractions: 0,
            totalTimeBetweenResponses: 0,
            countTimeBetweenResponses: 0,
            maxWaitTime: 0,
            negativeRatingsCount: 0,
            lastNegativeDate: null,
            ticketsUserClosedReopened: 0,
            firstHandledDate: null,
            lastHandledDate: null,
            daysInPeriod: 1 // Avoid division by zero
        };

        // Determine Days in Period for Averages
        if (dateFilter === 'daily') stats.daysInPeriod = 1;
        else if (dateFilter === 'weekly') stats.daysInPeriod = 7;
        else if (dateFilter === 'monthly') stats.daysInPeriod = 30;
        else if (dateFilter === 'all') {
            // Rough calc: mismatch between first/last ticket date or just default to 30 if null
            // We'll calculate dynamic range if needed, for now default 30 to normalize "all time" sort of? 
            // Actually for "all" it's better to show Totals or Avg per Month maybe? 
            // Requirement says "Resolvidos por dia/semana".
            // Let's rely on actual range of data if filter is ALL
            stats.daysInPeriod = 30; // Default fallback
        }

        const SLA_FIRST_RESPONSE_MS = 2 * 60 * 60 * 1000; // 2 Hours
        const SLA_RESOLUTION_MS = 48 * 60 * 60 * 1000;    // 48 Hours

        tickets.forEach(t => {
            const created = new Date(t.createdAt);

            // Staff Involvement Check
            const isAssigned = t.assignedTo === userId || (t.assignees && t.assignees.some(a => a.discordId === userId));
            const hasResponded = t.responses && t.responses.some(r => r && (String(r.userId) === String(userId) || ((r.author === userName || r.author === staffDisplayName) && r.isStaff)));

            if (!isAssigned && !hasResponded) return; // Not relevant to this staff

            // Relevance Checks
            const createdInPeriod = filterDate(t.createdAt);
            const updatedInPeriod = filterDate(t.lastUpdate || t.createdAt);
            // "Active in Period" = Updated or Created in period
            const activeInPeriod = createdInPeriod || updatedInPeriod;

            // --- Operational Metrics Calculation ---

            // 1. Tickets Assigned (Total Volume for Period)
            // We count tickets that were ASSIGNED to this user within the period, OR active tickets assigned to them.
            // Simplified: If assigned and active/created in period
            if (isAssigned && activeInPeriod) {
                stats.totalAssigned++;
            }

            // 2. Open Tickets (Snapshot of CURRENT backlog, arguably irrelevant of date filter but usually "Open tickets from period")
            // Requirement: "Tickets em aberto: Quantidade... que ainda não foram finalizados"
            // If date filter is active, we usually want "Backlog created in this period"? Or "Current backlog"?
            // Let's go with: "Current Open Tickets that were active in this period"
            if (isAssigned && (t.status === 'aberto' || t.status === 'em_andamento' || t.status === 'aguardando_resposta')) {
                stats.open++;
            }

            // 3. Finished Tickets
            if ((t.status === 'resolvido' || t.status === 'fechado')) {
                // Must be resolved IN the period
                // We use lastUpdate as proxy for resolution date if explicit date missing
                if (updatedInPeriod) {
                    stats.finished++;

                    // Resolution Time
                    const duration = new Date(t.lastUpdate) - new Date(t.createdAt);
                    if (duration > 0) {
                        stats.totalResolutionTime += duration;
                        stats.resolutionCount++;
                    }

                    // Check "Last Closed After User Msg"
                    const lastMsg = t.responses && t.responses[t.responses.length - 1];
                    if (lastMsg && lastMsg.isStaff === false) {
                        if (!stats.lastClosedAfterUserMsg || new Date(t.lastUpdate) > new Date(stats.lastClosedAfterUserMsg.date)) {
                            stats.lastClosedAfterUserMsg = { id: t.id, date: t.lastUpdate, title: t.title };
                        }
                    }
                }
            }

            // 4. Waiting User Response (Snapshot)
            if (isAssigned && (t.status === 'aberto' || t.status === 'em_andamento' || t.status === 'aguardando_resposta')) {
                const lastMsg = t.responses && t.responses.length > 0 ? t.responses[t.responses.length - 1] : null;
                if (lastMsg && (lastMsg.isStaff || String(lastMsg.userId) === String(userId))) {
                    stats.waitingUser++;
                }
            }

            // 5. Total Handled (Interaction in Period)
            if (activeInPeriod && hasResponded) {
                stats.totalHandledPeriod++;
            }

            // 6. Response Stats (First Response)
            if (t.responses) {
                const sorted = [...t.responses].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                const firstStaffMsg = sorted.find(r => r && r.isStaff && (String(r.userId) === String(userId) || r.author === userName || r.author === staffDisplayName));

                if (firstStaffMsg) {
                    // Check if the response was made IN the period (Performance of the period)
                    if (filterDate(firstStaffMsg.createdAt)) {
                        const time = new Date(firstStaffMsg.createdAt) - new Date(t.createdAt);
                        if (time > 0 && time < 1000 * 60 * 60 * 24 * 30) {
                            stats.totalResponseTime += time;
                            stats.firstResponseCount++;
                        }
                    }
                }

                // Message Volume
                const mymsgs = t.responses.filter(r => r && (String(r.userId) === String(userId) || r.author === userName || r.author === staffDisplayName));
                // Count messages sent IN period
                mymsgs.forEach(m => {
                    if (filterDate(m.createdAt)) stats.totalMessages++;
                });
            }

            // 7. Unique Users
            if (activeInPeriod) {
                if (t.uId) stats.uniqueUsers.add(t.uId);
                else if (t.userId) stats.uniqueUsers.add(t.userId);
            }

            // 8. Reopened Count (Log Scan)
            if (updatedInPeriod && t.logs) {
                t.logs.forEach(l => {
                    if (l.message && (l.message.toLowerCase().includes('reaberto') || l.message.includes('reabriu'))) {
                        if (filterDate(l.timestamp)) stats.reopened++;
                    }
                });
            }

            // 9. Ratings
            if (updatedInPeriod && t.feedback) {
                const score = Number(t.feedback.score || t.feedback.rating);
                if (!isNaN(score)) {
                    stats.ratings.count++;
                    stats.ratings.sum += score;
                    if (score >= 4) stats.ratings.positive++;
                    else if (score <= 2) stats.ratings.negative++;
                }
            }



            // --- SLA Calculations ---
            if (activeInPeriod) {
                let hasBreach = false;

                // 10. First Response SLA
                // Only check if there WAS a staff response
                if (t.responses) {
                    const sorted = [...t.responses].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                    const firstStaffMsg = sorted.find(r => r.isStaff && (String(r.userId) === String(userId) || r.author === userName || r.author === staffDisplayName));

                    if (firstStaffMsg) {
                        stats.slaFirstResponseTotal++;
                        const responseTime = new Date(firstStaffMsg.createdAt) - new Date(t.createdAt);
                        if (responseTime > SLA_FIRST_RESPONSE_MS) {
                            stats.slaFirstResponseBreaches++;
                            stats.totalSlaDelay += (responseTime - SLA_FIRST_RESPONSE_MS);
                            hasBreach = true;
                        }
                    }
                }

                // 11. Resolution SLA
                // Only check if ticket is resolved/closed
                if (updatedInPeriod && (t.status === 'resolvido' || t.status === 'fechado')) {
                    stats.slaResolutionTotal++;
                    const resolutionTime = new Date(t.lastUpdate) - new Date(t.createdAt);
                    if (resolutionTime > SLA_RESOLUTION_MS) {
                        stats.slaResolutionBreaches++;
                        stats.totalSlaDelay += (resolutionTime - SLA_RESOLUTION_MS);
                        hasBreach = true;
                    }
                }

                if (hasBreach) {
                    stats.ticketsBreachedSlaCount++;
                }
            }


            // --- Advanced Metrics Calculations ---
            if (activeInPeriod && hasResponded) {
                // 1. Interaction Counts & First/Last Handled
                const myResponses = t.responses ? t.responses.filter(r => String(r.userId) === String(userId) || r.author === userName || r.author === staffDisplayName) : [];
                const myResponseCount = myResponses.length;

                if (myResponseCount === 1) stats.ticketsOneResponse++;
                else if (myResponseCount > 1) stats.ticketsMultipleInteractions++;

                // Update First/Last Handled Dates
                myResponses.forEach(r => {
                    const rDate = new Date(r.createdAt);
                    if (!stats.firstHandledDate || rDate < stats.firstHandledDate) stats.firstHandledDate = rDate;
                    if (!stats.lastHandledDate || rDate > stats.lastHandledDate) stats.lastHandledDate = rDate;
                });

                // 2. Wait Times (Time Average Between Responses)
                // Logic: Time between USER message and NEXT STAFF message
                if (t.responses) {
                    const sorted = [...t.responses].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                    let lastUserMsgTime = new Date(t.createdAt).getTime(); // Initial wait starts at creation

                    sorted.forEach(msg => {
                        const msgTime = new Date(msg.createdAt).getTime();
                        if (msg && msg.isStaff) {
                            // It's a staff response. Calculate wait time from last user action.
                            if (lastUserMsgTime) {
                                const wait = msgTime - lastUserMsgTime;
                                if (wait > 0) {
                                    stats.totalTimeBetweenResponses += wait;
                                    stats.countTimeBetweenResponses++;
                                    if (wait > stats.maxWaitTime) stats.maxWaitTime = wait;
                                }
                                lastUserMsgTime = null; // Reset until next user msg
                            }
                        } else {
                            // User message, start timer if not already running (or update to latest user msg timestamp? Usually strict SLA is from FIRST user msg in a block)
                            // Simple version: from the *immediately preceding* user message block?
                            // Let's set start time if it's null.
                            if (!lastUserMsgTime) lastUserMsgTime = msgTime;
                        }
                    });
                }

                // 3. User Closed & Reopened
                // Check logs: "Ticket fechado pelo criador" followed by "reaberto" ?? 
                // Or just check if `t.closedBy === 'user'` (if exists) AND status is open? 
                // More reliably: Search logs for 'closed by user' then 'reopened'
                if (t.logs) {
                    let closedByUser = false;
                    let reopenedAfter = false;
                    t.logs.forEach(l => {
                        const msg = l.message ? l.message.toLowerCase() : "";
                        if (msg.includes('fechado pelo criador') || msg.includes('fechado pelo usuário')) closedByUser = true;
                        if (closedByUser && (msg.includes('reaberto') || msg.includes('reabriu'))) reopenedAfter = true;
                    });
                    if (reopenedAfter) stats.ticketsUserClosedReopened++;
                }

                // 4. Negative Ratings & Last Date
                if (t.feedback) {
                    const score = Number(t.feedback.score || t.feedback.rating);
                    if (!isNaN(score) && score <= 2) { // Negative threshold
                        stats.negativeRatingsCount++;
                        const feedbackDate = t.feedback.checkout_at ? new Date(t.feedback.checkout_at) : new Date(t.lastUpdate);
                        if (!stats.lastNegativeDate || feedbackDate > stats.lastNegativeDate) {
                            stats.lastNegativeDate = feedbackDate;
                        }
                    }
                }
            }
        });

        // Calc Date Range for 'ALL' filter if needed
        if (dateFilter === 'all' && stats.firstHandledDate && stats.lastHandledDate) {
            const diffTime = Math.abs(stats.lastHandledDate - stats.firstHandledDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            stats.daysInPeriod = diffDays || 1;
        }

        // Averages
        const avgFirstResponse = stats.firstResponseCount ? stats.totalResponseTime / stats.firstResponseCount : 0;
        const avgResolution = stats.resolutionCount ? stats.totalResolutionTime / stats.resolutionCount : 0;
        const avgRating = stats.ratings.count ? (stats.ratings.sum / stats.ratings.count).toFixed(1) : '—';
        const percentPositive = stats.ratings.count ? Math.round((stats.ratings.positive / stats.ratings.count) * 100) : 0;
        const percentNegative = stats.ratings.count ? Math.round((stats.ratings.negative / stats.ratings.count) * 100) : 0;

        return {
            ...stats,
            avgFirstResponse,
            avgResolution,
            avgRating,
            percentPositive,
            percentNegative,
            uniqueUsersCount: stats.uniqueUsers.size,
            // SLA Derived Metrics
            slaFirstResponsePercent: stats.slaFirstResponseTotal ? Math.round(((stats.slaFirstResponseTotal - stats.slaFirstResponseBreaches) / stats.slaFirstResponseTotal) * 100) : 100,
            slaResolutionPercent: stats.slaResolutionTotal ? Math.round(((stats.slaResolutionTotal - stats.slaResolutionBreaches) / stats.slaResolutionTotal) * 100) : 100,
            avgSlaDelay: stats.ticketsBreachedSlaCount ? stats.totalSlaDelay / stats.ticketsBreachedSlaCount : 0,

            // Advanced Derived
            avgMessagesPerTicket: stats.totalHandledPeriod ? (stats.totalMessages / stats.totalHandledPeriod).toFixed(1) : 0,
            avgResolvedPerDay: (stats.finished / stats.daysInPeriod).toFixed(1),
            avgResolvedPerWeek: (stats.finished / (stats.daysInPeriod / 7)).toFixed(1),
            avgTimeBetweenResponses: stats.countTimeBetweenResponses ? stats.totalTimeBetweenResponses / stats.countTimeBetweenResponses : 0,
            maxWaitTime: stats.maxWaitTime,
            ticketsOneResponse: stats.ticketsOneResponse,
            ticketsMultipleInteractions: stats.ticketsMultipleInteractions,
            ticketsUserClosedReopened: stats.ticketsUserClosedReopened,
            negativeRatingsCount: stats.negativeRatingsCount,
            lastNegativeDate: stats.lastNegativeDate,
            firstHandledDate: stats.firstHandledDate,
            lastHandledDate: stats.lastHandledDate
        };

    }, [selectedUser, tickets, punishments, dateFilter, selectedDate]);

    // Format Helper
    const formatDuration = (ms) => {
        if (!ms) return '-';
        const days = Math.floor(ms / (1000 * 60 * 60 * 24));
        const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    if (!selectedUser) return <div className="text-white">Selecione um membro da staff.</div>;

    const dateLabel = dateFilter === 'daily' ? format(selectedDate, "dd 'de' MMMM", { locale: ptBR }) :
        dateFilter === 'weekly' ? 'Esta Semana' :
            dateFilter === 'monthly' ? format(selectedDate, "MMMM 'de' yyyy", { locale: ptBR }) :
                'Todo o Período';

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header / Filter Bar */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-black/40 p-4 rounded-xl border border-white/10 backdrop-blur-md">
                {/* Staff Selector */}
                <div className="flex-1 w-full md:w-auto">
                    <label className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1.5 block">
                        Membro da Equipe
                    </label>
                    <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                        <PopoverTrigger asChild>
                            <Button variant="outline" role="combobox" aria-expanded={openCombobox} className="w-full md:w-[280px] justify-between bg-white/5 border-white/10 text-white hover:bg-white/10">
                                {selectedUser ? getStaffDisplayName(selectedUser) : "Selecione..."}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[280px] p-0 bg-gray-950 border-white/10 text-white">
                            <Command>
                                <CommandInput placeholder="Buscar staff..." className="text-white" />
                                <CommandList>
                                    <CommandEmpty>Nenhum membro encontrado.</CommandEmpty>
                                    <CommandGroup>
                                        {staffList.map((staff) => (
                                            <CommandItem
                                                key={staff.discordId}
                                                value={staff.name}
                                                onSelect={() => {
                                                    setSelectedStaffId(staff.discordId);
                                                    setOpenCombobox(false);
                                                }}
                                                className="aria-selected:bg-white/10 cursor-pointer"
                                            >
                                                <Check className={cn("mr-2 h-4 w-4", selectedStaffId === staff.discordId ? "opacity-100" : "opacity-0")} />
                                                {getStaffDisplayName(staff)}
                                                <Badge variant="secondary" className="ml-auto text-[10px] h-5">
                                                    {staff.accessLevel}
                                                </Badge>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                {/* Date Filters */}
                <div className="flex items-center gap-2 bg-white/5 p-1 rounded-lg border border-white/10">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDateFilter('daily')}
                        className={cn("text-xs uppercase", dateFilter === 'daily' ? 'bg-indigo-500/20 text-indigo-400' : 'text-gray-400 hover:text-white')}
                    >
                        Diário
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDateFilter('weekly')}
                        className={cn("text-xs uppercase", dateFilter === 'weekly' ? 'bg-indigo-500/20 text-indigo-400' : 'text-gray-400 hover:text-white')}
                    >
                        Semanal
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDateFilter('monthly')}
                        className={cn("text-xs uppercase", dateFilter === 'monthly' ? 'bg-indigo-500/20 text-indigo-400' : 'text-gray-400 hover:text-white')}
                    >
                        Mensal
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDateFilter('all')}
                        className={cn("text-xs uppercase", dateFilter === 'all' ? 'bg-indigo-500/20 text-indigo-400' : 'text-gray-400 hover:text-white')}
                    >
                        Tudo
                    </Button>
                </div>
            </div>

            {/* Context Label */}
            <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-indigo-400" />
                <span className="text-sm font-bold text-indigo-400/80 uppercase tracking-widest">
                    Performance - {dateLabel}
                </span>
            </div>

            {/* --- OPERATIONAL METRICS PANEL --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* 1. Open / Backlog */}
                <StatCard
                    title="Tickets em Aberto"
                    value={metrics?.open}
                    icon={Ticket}
                    color="text-blue-400"
                    bg="bg-blue-500/10"
                    subtext="Atribuídos e Pendentes"
                    tooltip="Quantidade de tickets atualmente atribuídos a você que estão em aberto, em progresso ou aguardando resposta."
                />

                {/* 2. Finished */}
                <StatCard
                    title="Finalizados no Período"
                    value={metrics?.finished}
                    icon={CheckCircle2}
                    color="text-green-400"
                    bg="bg-green-500/10"
                    subtext="Resolvidos/Fechados"
                    tooltip="Total de tickets que foram marcados como resolvidos ou fechados dentro do período de tempo selecionado."
                />

                {/* 3. Waiting User */}
                <StatCard
                    title="Aguardando Usuário"
                    value={metrics?.waitingUser}
                    icon={Clock}
                    color="text-yellow-400"
                    bg="bg-yellow-500/10"
                    subtext="Última resposta do Staff"
                    tooltip="Tickets onde a última interação foi sua (staff) e que estão aguardando um retorno do usuário."
                />

                {/* 4. Reopened */}
                <StatCard
                    title="Tickets Reabertos"
                    value={metrics?.reopened}
                    icon={AlertTriangle}
                    color="text-red-400"
                    bg="bg-red-500/10"
                    subtext="Retornaram ao fluxo"
                    tooltip="Quantidade de tickets que foram fechados e posteriormente reabertos pelo usuário ou staff dentro do período."
                />
            </div>

            {/* --- SLA METRICS PANEL --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="SLA Resposta"
                    value={`${metrics?.slaFirstResponsePercent}%`}
                    icon={Clock}
                    color={metrics?.slaFirstResponsePercent >= 90 ? "text-green-400" : metrics?.slaFirstResponsePercent >= 70 ? "text-yellow-400" : "text-red-400"}
                    bg={metrics?.slaFirstResponsePercent >= 90 ? "bg-green-500/10" : metrics?.slaFirstResponsePercent >= 70 ? "bg-yellow-500/10" : "bg-red-500/10"}
                    subtext="Meta: 2 Horas"
                    tooltip="Porcentagem de tickets atendidos dentro do prazo de 2 horas para a primeira resposta."
                />
                <StatCard
                    title="SLA Resolução"
                    value={`${metrics?.slaResolutionPercent}%`}
                    icon={CheckCircle2}
                    color={metrics?.slaResolutionPercent >= 90 ? "text-green-400" : metrics?.slaResolutionPercent >= 70 ? "text-yellow-400" : "text-red-400"}
                    bg={metrics?.slaResolutionPercent >= 90 ? "bg-green-500/10" : metrics?.slaResolutionPercent >= 70 ? "bg-yellow-500/10" : "bg-red-500/10"}
                    subtext="Meta: 48 Horas"
                    tooltip="Porcentagem de tickets resolvidos dentro do prazo de 48 horas."
                />
                <StatCard
                    title="Fora do SLA"
                    value={metrics?.ticketsBreachedSlaCount}
                    icon={AlertTriangle}
                    color="text-red-400"
                    bg="bg-red-500/10"
                    subtext="Tickets com atraso"
                    tooltip="Quantidade de tickets que estouraram o tempo limite de resposta (2h) ou resolução (48h)."
                />
                <StatCard
                    title="Tempo Médio Atraso"
                    value={formatDuration(metrics?.avgSlaDelay)}
                    icon={TrendingUp}
                    color="text-orange-400"
                    bg="bg-orange-500/10"
                    subtext="Média excedente"
                    tooltip="Tempo médio que os tickets ultrapassaram o prazo estipulado quando houve atraso."
                />
            </div>

            {/* --- ADVANCED METRICS PANEL --- */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2 mt-4">
                    <BarChart3 className="h-4 w-4 text-indigo-400" />
                    <span className="text-sm font-bold text-indigo-400/80 uppercase tracking-widest">
                        Métricas Avançadas
                    </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Interactivity */}
                    <StatCard
                        title="Msgs/Ticket"
                        value={metrics?.avgMessagesPerTicket}
                        icon={MessageSquare}
                        color="text-purple-400"
                        bg="bg-purple-500/10"
                        subtext="Média de interações"
                        tooltip="Média de mensagens enviadas pelo staff por ticket atendido."
                    />
                    <StatCard
                        title="Tickets 1 Resposta"
                        value={metrics?.ticketsOneResponse}
                        icon={Target}
                        color="text-pink-400"
                        bg="bg-pink-500/10"
                        subtext="Resolução Única"
                        tooltip="Quantidade de tickets onde o staff enviou apenas uma resposta."
                    />
                    <StatCard
                        title="Múltiplas Interações"
                        value={metrics?.ticketsMultipleInteractions}
                        icon={Users}
                        color="text-indigo-400"
                        bg="bg-indigo-500/10"
                        subtext="Diálogo Extenso"
                        tooltip="Quantidade de tickets com 2 ou mais respostas do staff."
                    />
                    <StatCard
                        title="Fechou/Reabriu"
                        value={metrics?.ticketsUserClosedReopened}
                        icon={AlertTriangle}
                        color="text-amber-400"
                        bg="bg-amber-500/10"
                        subtext="Ação do Usuário"
                        tooltip="Tickets que o usuário encerrou, mas depois reabriu."
                    />

                    {/* Timing & Flow */}
                    <StatCard
                        title="Resolvidos/Dia"
                        value={metrics?.avgResolvedPerDay}
                        icon={Calendar}
                        color="text-teal-400"
                        bg="bg-teal-500/10"
                        subtext="Média Diária"
                        tooltip="Média de tickets finalizados por dia no período selecionado."
                    />
                    <StatCard
                        title="Resolvidos/Semana"
                        value={metrics?.avgResolvedPerWeek}
                        icon={Calendar}
                        color="text-cyan-400"
                        bg="bg-cyan-500/10"
                        subtext="Média Semanal"
                        tooltip="Média de tickets finalizados por semana no período selecionado."
                    />
                    <StatCard
                        title="Tempo Entre Respostas"
                        value={formatDuration(metrics?.avgTimeBetweenResponses)}
                        icon={Clock}
                        color="text-blue-400"
                        bg="bg-blue-500/10"
                        subtext="Agilidade de Diálogo"
                        tooltip="Tempo médio decorrido entre uma mensagem do usuário e a resposta subsequente do staff."
                    />
                    <StatCard
                        title="Maior Espera"
                        value={formatDuration(metrics?.maxWaitTime)}
                        icon={Clock}
                        color="text-red-400"
                        bg="bg-red-500/10"
                        subtext="Pior Caso"
                        tooltip="O maior tempo que um usuário aguardou por uma resposta neste período."
                    />

                    {/* Quality Negative Focus */}
                    <StatCard
                        title="Negativas (<=2)"
                        value={metrics?.negativeRatingsCount}
                        icon={Shield}
                        color="text-red-500"
                        bg="bg-red-500/10"
                        subtext="Notas Baixas"
                        tooltip="Total de avaliações com nota 1 ou 2 estrelas recebidas no período."
                    />
                    <StatCard
                        title="Última Negativa"
                        value={metrics?.lastNegativeDate ? format(metrics.lastNegativeDate, "dd/MM") : "-"}
                        icon={AlertTriangle}
                        color="text-orange-400"
                        bg="bg-orange-500/10"
                        subtext="Data Ocorrência"
                        tooltip="Data da última avaliação negativa recebida."
                    />
                    <StatCard
                        title="1º Atendimento"
                        value={metrics?.firstHandledDate ? format(metrics.firstHandledDate, "dd/MM HH:mm") : "-"}
                        icon={Calendar}
                        color="text-gray-400"
                        bg="bg-white/5"
                        subtext="Início Período"
                        tooltip="Data e hora da primeira interação registrada no período."
                    />
                    <StatCard
                        title="Último Atendimento"
                        value={metrics?.lastHandledDate ? format(metrics.lastHandledDate, "dd/MM HH:mm") : "-"}
                        icon={Calendar}
                        color="text-gray-400"
                        bg="bg-white/5"
                        subtext="Fim Período"
                        tooltip="Data e hora da última interação registrada no período."
                    />
                </div>
            </div>

            {/* Attention Alert: Last Closed after User Msg */}
            {metrics?.lastClosedAfterUserMsg && (
                <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-center justify-between animate-in fade-in slide-in-from-left-2">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-red-500/20 flex items-center justify-center">
                            <AlertTriangle className="h-5 w-5 text-red-400" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-red-200">Atenção: Ticket encerrado após mensagem do usuário</p>
                            <p className="text-xs text-red-400/80 mt-0.5">
                                O ticket <strong>#{metrics.lastClosedAfterUserMsg.title}</strong> foi fechado sem resposta final do staff em {format(new Date(metrics.lastClosedAfterUserMsg.date), "dd/MM HH:mm")}.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Performance & Quality Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Productivity Logic */}
                <Card className="lg:col-span-2 bg-black/40 border-white/10 backdrop-blur-md">
                    <CardHeader>
                        <CardTitle className="text-white text-lg flex items-center gap-2">
                            <Activity className="h-5 w-5 text-indigo-400" />
                            Produtividade Operacional
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                            <span className="text-xs text-gray-500 uppercase font-bold">Total Atribuídos</span>
                            <div className="text-2xl font-bold text-white mt-1">{metrics?.totalAssigned}</div>
                            <span className="text-xs text-gray-600">No período selecionado</span>
                        </div>
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                            <span className="text-xs text-gray-500 uppercase font-bold">Total Atendidos</span>
                            <div className="text-2xl font-bold text-white mt-1">{metrics?.totalHandledPeriod}</div>
                            <span className="text-xs text-gray-600">Com interação do staff</span>
                        </div>
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                            <span className="text-xs text-gray-500 uppercase font-bold">Tempo Médio 1ª Resposta</span>
                            <div className="text-2xl font-bold text-cyan-400 mt-1">{formatDuration(metrics?.avgFirstResponse)}</div>
                        </div>
                        <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                            <span className="text-xs text-gray-500 uppercase font-bold">Tempo Médio Encerramento</span>
                            <div className="text-2xl font-bold text-emerald-400 mt-1">{formatDuration(metrics?.avgResolution)}</div>
                        </div>
                    </CardContent>
                </Card>

                {/* Quality & Ratings */}
                <Card className="bg-black/40 border-white/10 backdrop-blur-md">
                    <CardHeader>
                        <CardTitle className="text-white text-lg flex items-center gap-2">
                            <Star className="h-5 w-5 text-yellow-400" />
                            Qualidade de Atendimento
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="text-center py-4 bg-white/5 rounded-2xl border border-white/5">
                            <div className="text-4xl font-bold text-white mb-1">{metrics?.avgRating}</div>
                            <div className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Nota Média</div>
                            <div className="flex justify-center gap-1 mt-3">
                                {[1, 2, 3, 4, 5].map(s => (
                                    <Star key={s} className={`h-4 w-4 ${s <= Number(metrics?.avgRating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-800'}`} />
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between text-xs mb-1.5">
                                    <span className="text-green-400 font-bold">Positivas</span>
                                    <span className="text-gray-400">{metrics?.percentPositive}%</span>
                                </div>
                                <Progress value={metrics?.percentPositive} className="h-1.5 bg-white/5" indicatorClassName="bg-green-500" />
                            </div>
                            <div>
                                <div className="flex justify-between text-xs mb-1.5">
                                    <span className="text-red-400 font-bold">Negativas</span>
                                    <span className="text-gray-400">{metrics?.percentNegative}%</span>
                                </div>
                                <Progress value={metrics?.percentNegative} className="h-1.5 bg-white/5" indicatorClassName="bg-red-500" />
                            </div>
                            <div className="pt-2 border-t border-white/5">
                                <p className="text-center text-xs text-gray-500">
                                    Total de {metrics?.ratings.count} avaliações
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

const InsightItem = ({ label, value, icon: Icon, color }) => (
    <div className="p-3 rounded-lg bg-white/5 border border-white/5 hover:bg-white/10 transition-colors">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-xs text-gray-500 uppercase font-bold mb-1">{label}</p>
                <p className="text-lg font-bold text-white">{value}</p>
            </div>
            <Icon className={`h-4 w-4 ${color} opacity-80`} />
        </div>
    </div>
);

const StatCard = ({ title, value, icon: Icon, color, bg, subtext, tooltip }) => (
    <Card className="bg-black/40 border-white/10 backdrop-blur-md hover:bg-white/5 transition-all group">
        <CardContent className="p-6">
            <div className="flex justify-between items-start">
                <div>
                    <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-400">{title}</p>
                        {tooltip && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Info className="h-4 w-4 text-gray-500 hover:text-white cursor-pointer transition-colors" />
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-gray-900 border-white/10 text-white p-3 text-xs max-w-[200px]">
                                        <p>{tooltip}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                    <h3 className="text-3xl font-bold text-white mt-2">{value}</h3>
                    <p className="text-xs text-gray-500 mt-1">{subtext}</p>
                </div>
                <div className={`p-3 rounded-xl ${bg} group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-6 h-6 ${color}`} />
                </div>
            </div>
        </CardContent>
    </Card>
);

export default IndividualTab;
